import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { NAV_GROUPS, NAV_STANDALONE, isNavItemOffered, type NavItem } from '@/lib/nav';
import type { Ability } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';

/* -------------------------------------------------------------------------- */
/* Fuzzy matching                                                             */
/* -------------------------------------------------------------------------- */

/*
 * A hand-written subsequence scorer — no dependency, and the rules are ours to
 * tune. It ranks, it does not filter: a record the server already matched is a
 * real match whatever this says, so failing here only costs that row its
 * highlight.
 *
 * Cost is O(needle x haystack^2), which is why the haystack is clipped and a
 * cheap subsequence test runs first. Labels are short (a name, a nav row), so
 * in practice this is a few thousand operations per keystroke.
 */
const MAX_HAYSTACK = 96;
const CONTIGUOUS_BONUS = 6;
const WORD_START_BONUS = 9;
const EXACT_CASE_BONUS = 1;
const GAP_PENALTY = 0.6;
const MAX_GAP_PENALTY = 4;
const LEAD_PENALTY = 0.4;
const MAX_LEAD_PENALTY = 4;

interface FuzzyMatch {
  score: number;
  /** Indices into the haystack that the needle matched, ascending. */
  indices: number[];
}

function isAlphanumeric(code: number): boolean {
  return (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
}

/** True at the head of a word — start of string, after punctuation, or a camelCase hump. */
function isWordStart(text: string, index: number): boolean {
  if (index === 0) return true;
  const previous = text.charCodeAt(index - 1);
  if (!isAlphanumeric(previous)) return true;
  const current = text.charCodeAt(index);
  return previous >= 97 && previous <= 122 && current >= 65 && current <= 90;
}

/**
 * Best-scoring subsequence match of `needle` in `haystack`, or null when the
 * needle is not a subsequence at all. Case-insensitive; contiguous runs and
 * word starts score higher, gaps and a late first match score lower.
 *
 * Deliberately not exported: this file exports components, and a second export
 * costs the whole module its fast refresh.
 */
function fuzzyMatch(haystack: string, needle: string): FuzzyMatch | null {
  const query = needle.trim();
  if (query === '') return { score: 0, indices: [] };

  const text = haystack.slice(0, MAX_HAYSTACK);
  const n = query.length;
  const m = text.length;
  if (n > m) return null;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  // Cheap reject before the quadratic pass — most rows fail here.
  let seen = 0;
  for (let j = 0; j < m && seen < n; j += 1) {
    if (lowerText[j] === lowerQuery[seen]) seen += 1;
  }
  if (seen < n) return null;

  const NONE = Number.NEGATIVE_INFINITY;
  const score = new Float64Array(n * m).fill(NONE);
  const cameFrom = new Int32Array(n * m).fill(-1);

  for (let i = 0; i < n; i += 1) {
    for (let j = i; j < m; j += 1) {
      if (lowerText[j] !== lowerQuery[i]) continue;

      let cell = 1;
      if (isWordStart(text, j)) cell += WORD_START_BONUS;
      if (text[j] === query[i]) cell += EXACT_CASE_BONUS;

      if (i === 0) {
        // A first match at character 40 is a weaker signal than one at 0.
        score[j] = cell - Math.min(j * LEAD_PENALTY, MAX_LEAD_PENALTY);
        continue;
      }

      let best = NONE;
      let bestPrevious = -1;
      const previousRow = (i - 1) * m;
      for (let p = i - 1; p < j; p += 1) {
        const previous = score[previousRow + p] ?? NONE;
        if (previous === NONE) continue;
        const gap = j - p - 1;
        const candidate =
          previous +
          (gap === 0 ? CONTIGUOUS_BONUS : -Math.min(gap * GAP_PENALTY, MAX_GAP_PENALTY));
        if (candidate > best) {
          best = candidate;
          bestPrevious = p;
        }
      }
      if (bestPrevious === -1) continue;
      score[i * m + j] = best + cell;
      cameFrom[i * m + j] = bestPrevious;
    }
  }

  let bestEnd = -1;
  let bestScore = NONE;
  const lastRow = (n - 1) * m;
  for (let j = n - 1; j < m; j += 1) {
    const candidate = score[lastRow + j] ?? NONE;
    if (candidate > bestScore) {
      bestScore = candidate;
      bestEnd = j;
    }
  }
  if (bestEnd === -1) return null;

  const indices = new Array<number>(n);
  let cursor = bestEnd;
  for (let i = n - 1; i >= 0; i -= 1) {
    indices[i] = cursor;
    cursor = cameFrom[i * m + cursor] ?? -1;
  }
  return { score: bestScore, indices };
}

/** Renders `text` with the matched characters marked. Runs are merged, not per character. */
function Highlighted({ text, indices }: { text: string; indices: number[] }) {
  if (indices.length === 0) return <>{text}</>;
  const matched = new Set(indices);
  const nodes: ReactNode[] = [];
  let i = 0;
  while (i < text.length) {
    const on = matched.has(i);
    let j = i;
    while (j < text.length && matched.has(j) === on) j += 1;
    const chunk = text.slice(i, j);
    nodes.push(
      on ? (
        <mark key={i} className="rounded-[2px] bg-accent-brand-soft px-px font-semibold text-fg">
          {chunk}
        </mark>
      ) : (
        <span key={i}>{chunk}</span>
      ),
    );
    i = j;
  }
  return <>{nodes}</>;
}

/* -------------------------------------------------------------------------- */
/* Result model                                                               */
/* -------------------------------------------------------------------------- */

interface SearchResult {
  key: string;
  label: string;
  /** Second line — whatever separates two records with the same name. */
  hint?: string;
  to: string;
  icon?: ReactNode;
}

interface RankedResult extends SearchResult {
  score: number;
  indices: number[];
}

/** A picker, not a report: eight rows per entity is plenty to recognise one. */
const RECORD_LIMIT = 8;
/** One character matches most of the database; the request is not worth making. */
const MIN_TERM = 2;

interface RecordSource {
  key: string;
  title: string;
  /** Says what the API's `search` actually covers when it is narrower than it looks. */
  note?: string;
  load: (term: string, signal: AbortSignal) => Promise<SearchResult[]>;
}

/*
 * Only endpoints that really implement `search`. Cycles and intakes do NOT —
 * and this API silently strips unknown query params, so asking them to search
 * would return an unfiltered first page that looks exactly like a set of
 * matches. They are excluded, and the palette's footnote says so.
 */
const RECORD_SOURCES: readonly RecordSource[] = [
  {
    key: 'companies',
    title: 'Companies',
    load: async (term, signal) => {
      const page = await api.listCompanies({ search: term, perPage: RECORD_LIMIT }, signal);
      return page.data.map((row) => ({
        key: `company-${row.id}`,
        label: row.name,
        hint: row.email ?? undefined,
        to: `/companies/${row.id}`,
      }));
    },
  },
  {
    key: 'contacts',
    title: 'Contacts',
    load: async (term, signal) => {
      const page = await api.listContacts({ search: term, perPage: RECORD_LIMIT }, signal);
      return page.data.map((row) => ({
        key: `contact-${row.id}`,
        label: row.name,
        // The company separates two people with the same name faster than an email.
        hint: row.company?.name ?? row.email ?? undefined,
        to: `/contacts/${row.id}`,
      }));
    },
  },
  {
    key: 'deals',
    title: 'Deals',
    note: 'Matches the deal name only.',
    load: async (term, signal) => {
      const page = await api.listDeals({ search: term, perPage: RECORD_LIMIT }, signal);
      return page.data.map((row) => ({
        key: `deal-${row.id}`,
        label: row.dealName,
        // Every embed on a deal is nullable.
        hint: row.company?.name ?? row.stage?.name ?? undefined,
        to: `/deals/${row.id}`,
      }));
    },
  },
  /*
   * ⚠️ **The `deliverables` group was REMOVED with the pages behind it.** Every
   * result it produced pointed at `/deliverables/:id`, which left the route
   * table when the old hand-entered entity was deleted — so the group would
   * have kept searching, kept rendering rows, and sent every click to the 404
   * page. `api.listDeliverables` still exists and the endpoint still answers;
   * there is simply nowhere in this app for a result to open.
   *
   * ⚠️ **Nothing here could have caught that.** A search group is a data
   * literal and `to` is a string: `tsc` was green, oxlint was green, and the
   * only symptom would have been a dead click on results that no longer exist
   * anywhere else in the UI. It is NOT replaced by a `/client-deliverables`
   * group — that tracker's rows are derived line items with no detail page and
   * no id to open, so there would be nothing for a result to link to.
   */
  {
    key: 'products',
    title: 'Products',
    note: 'Matches the product name only, and opens the Products list — a product has no page of its own.',
    load: async (term, signal) => {
      const page = await api.listProducts({ search: term, perPage: RECORD_LIMIT }, signal);
      return page.data.map((row) => ({
        key: `product-${row.id}`,
        label: row.productName,
        hint: row.productCode ?? row.productCategory ?? undefined,
        to: '/products',
      }));
    },
  },
  {
    key: 'users',
    title: 'Users',
    note: 'Opens the Users list — there is no page per user.',
    load: async (term, signal) => {
      const page = await api.listUsers({ search: term, perPage: RECORD_LIMIT }, signal);
      return page.data.map((row) => ({
        key: `user-${row.id}`,
        label: row.name,
        hint: row.email,
        to: '/users',
      }));
    },
  },
];

interface GroupState {
  loading: boolean;
  error: string | null;
  results: SearchResult[];
}

const IDLE_GROUP: GroupState = { loading: false, error: null, results: [] };

function idleGroups(): Record<string, GroupState> {
  return Object.fromEntries(RECORD_SOURCES.map((source) => [source.key, IDLE_GROUP]));
}

/* -------------------------------------------------------------------------- */
/* Navigation targets                                                         */
/* -------------------------------------------------------------------------- */

/*
 * 🔴 **BUILT PER READER, NOT AT MODULE LOAD — and that was the bug.** This list
 * used to be flattened once at import, where there is no ability to consult, so
 * it filtered on `Boolean(item.to)` and nothing else: the palette handed every
 * reader a link to every routed page, **including the two dashboards and the
 * trackers their permission set explicitly denies.** The sidebar hid the row and
 * the palette gave it straight back.
 *
 * ⚠️ **The three tests are `isNavItemOffered`'s, not a second copy** — read its
 * header in `@/lib/nav`. It DEGRADES OPEN, which is what stops the palette
 * emptying itself while `/permissions/me` is in flight.
 *
 * ⚠️ **A view control, never access control.** These pages have no server guard
 * behind them; this decides what is offered, not what is permitted.
 *
 * The external-destination test the sidebar `&&`s on is irrelevant here: an
 * external row carries no `to` and the `to` filter below has already dropped it.
 * Only rows carrying a `to` survive at all — a nav entry without one renders as
 * the inert "Soon" treatment in the sidebar, and a palette row that navigates
 * nowhere is worse than no row.
 */
function navTargets(ability: Ability): readonly SearchResult[] {
  const routed = (items: readonly NavItem[]) =>
    items.filter(
      (item): item is NavItem & { to: string } =>
        Boolean(item.to) && isNavItemOffered(item, ability),
    );

  return [
    ...NAV_GROUPS.flatMap((group) =>
      routed(group.items).map((item) => ({
        key: `nav-${item.to}`,
        // The full product name where the sidebar had to abbreviate it.
        label: item.fullLabel ?? item.label,
        hint: group.label,
        to: item.to,
        icon: item.icon,
      })),
    ),
    ...routed(NAV_STANDALONE).map((item) => ({
      key: `nav-${item.to}`,
      label: item.fullLabel ?? item.label,
      to: item.to,
      icon: item.icon,
    })),
  ];
}

/* -------------------------------------------------------------------------- */
/* Icons                                                                      */
/* -------------------------------------------------------------------------- */

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function SearchIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg {...iconProps} className={className}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function EnterIcon() {
  return (
    <svg {...iconProps} className="h-3.5 w-3.5 shrink-0">
      <path d="M20 5v6a3 3 0 0 1-3 3H4" />
      <path d="m8 10-4 4 4 4" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Palette                                                                    */
/* -------------------------------------------------------------------------- */

function rank(results: readonly SearchResult[], term: string): RankedResult[] {
  if (term === '') return results.map((result) => ({ ...result, score: 0, indices: [] }));
  return results
    .map((result) => {
      const match = fuzzyMatch(result.label, term);
      /*
       * Server rows are kept even when the label does not fuzzy-match: the API
       * matched something (an unescaped LIKE, and on several endpoints across
       * columns we do not show), and dropping the row would be the client
       * second-guessing the endpoint.
       */
      return { ...result, score: match?.score ?? 0, indices: match?.indices ?? [] };
    })
    .sort((a, b) => b.score - a.score);
}

interface RenderGroup {
  key: string;
  title: string;
  note?: string;
  loading: boolean;
  error: string | null;
  results: RankedResult[];
}

function GlobalSearchPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { ability } = useAuth();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [term, setTerm] = useState('');
  const [groups, setGroups] = useState<Record<string, GroupState>>(idleGroups);
  const [activeIndex, setActiveIndex] = useState(0);

  const trimmed = term.trim();
  const searching = trimmed.length >= MIN_TERM;

  /*
   * One debounced round of requests, one AbortSignal shared by all of them, and
   * each group settling on its own — a 500 on deliverables must not blank the
   * companies that already arrived.
   */
  useEffect(() => {
    if (!open) return;
    /*
     * Below the threshold nothing is fetched and nothing is reset: the record
     * groups aren't rendered at all in that state, so clearing them here would
     * only be a second render nobody sees.
     */
    if (!searching) return;

    const controller = new AbortController();
    const { signal } = controller;

    const timer = setTimeout(() => {
      setGroups(
        Object.fromEntries(
          RECORD_SOURCES.map((source) => [source.key, { loading: true, error: null, results: [] }]),
        ),
      );

      for (const source of RECORD_SOURCES) {
        source
          .load(trimmed, signal)
          .then((results) => {
            if (signal.aborted) return;
            setGroups((current) => ({
              ...current,
              [source.key]: { ...(current[source.key] ?? IDLE_GROUP), error: null, results },
            }));
          })
          .catch((error: unknown) => {
            if (signal.aborted) return;
            setGroups((current) => ({
              ...current,
              [source.key]: {
                ...(current[source.key] ?? IDLE_GROUP),
                error: error instanceof Error ? error.message : 'Request failed',
                results: [],
              },
            }));
          })
          .finally(() => {
            if (signal.aborted) return;
            setGroups((current) => ({
              ...current,
              [source.key]: { ...(current[source.key] ?? IDLE_GROUP), loading: false },
            }));
          });
      }
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, searching, trimmed]);

  /*
   * Rebuilt when the ability changes and not per keystroke. The ability object
   * is stable for a session, so in practice this runs once — but it must be a
   * dependency rather than read outside, or a permission set arriving after
   * first paint would leave the palette on the list it built before it.
   */
  const navTargetList = useMemo(() => navTargets(ability), [ability]);

  const renderGroups = useMemo<RenderGroup[]>(() => {
    // Local and instant: nav targets never wait on a request.
    const navGroup: RenderGroup = {
      key: 'navigation',
      title: 'Go to',
      loading: false,
      error: null,
      results: rank(
        trimmed === ''
          ? navTargetList
          : navTargetList.filter((item) => fuzzyMatch(item.label, trimmed) !== null),
        trimmed,
      ),
    };

    if (!searching) return [navGroup];

    return [
      navGroup,
      ...RECORD_SOURCES.map((source) => {
        const state = groups[source.key] ?? IDLE_GROUP;
        return {
          key: source.key,
          title: source.title,
          note: source.note,
          loading: state.loading,
          error: state.error,
          results: rank(state.results, trimmed),
        };
      }),
    ];
  }, [groups, navTargetList, searching, trimmed]);

  // One flat list behind the grouped rendering — arrow keys cross group borders.
  const flat = useMemo(() => renderGroups.flatMap((group) => group.results), [renderGroups]);
  const indexByKey = useMemo(
    () => new Map(flat.map((result, index) => [result.key, index])),
    [flat],
  );

  /*
   * Clamped during render rather than corrected in an effect: a slower group
   * landing can shorten the list under a stored index, and fixing it here
   * avoids an extra render pass and a highlight on nothing.
   */
  const active = flat.length === 0 ? 0 : Math.min(activeIndex, flat.length - 1);
  const activeKey = flat[active]?.key;

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeKey]);

  const anyLoading = renderGroups.some((group) => group.loading);

  const go = useCallback(
    (result: SearchResult) => {
      onClose();
      navigate(result.to);
    },
    [navigate, onClose],
  );

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const result = flat[active];
      if (result) go(result);
      return;
    }
    /*
     * Escape is deliberately not handled here: it bubbles to Modal, which
     * closes and hands focus back to whatever opened the palette.
     */
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (flat.length === 0) return;
    if (event.key === 'Home') {
      setActiveIndex(0);
      return;
    }
    if (event.key === 'End') {
      setActiveIndex(flat.length - 1);
      return;
    }
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    setActiveIndex((active + delta + flat.length) % flat.length);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Search SEED EMERGE"
      size="md"
      initialFocusRef={inputRef}
      /*
       * ⛔ **THE FOOTER WAS EMPTIED — owner's helper-text ruling, 2026-09-05.**
       * Its surviving line read `Cycles and intakes are not searched.`
       * (the keyboard line went in the first pass, on 2026-09-04).
       *
       * ⚠️ **THE SCOPE LIMIT IS REAL AND IS NOW UNSTATED.** Cycles and intakes
       * are still not searched, so a search for one returns "No matches" —
       * indistinguishable from that record not existing. `/cycles` and
       * `/intakes` have no nav row either (they are reached from `/catalog`), so
       * a reader who cannot find a cycle here has no second place to look.
       */
    >
      <div className="flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 focus-within:border-hairline-strong">
        <span className="text-fg-subtle">
          <SearchIcon />
        </span>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeKey ? `${listId}-${activeKey}` : undefined}
          aria-label="Search SEED EMERGE"
          autoComplete="off"
          spellCheck={false}
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Search pages and records…"
          className="h-9 min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle max-sm:h-11"
        />
      </div>

      <p aria-live="polite" className="sr-only">
        {anyLoading
          ? 'Searching'
          : `${flat.length} ${flat.length === 1 ? 'result' : 'results'}${
              trimmed === '' ? '' : ` for ${trimmed}`
            }`}
      </p>

      <div
        id={listId}
        ref={listRef}
        role="listbox"
        aria-label="Search results"
        className="mt-2 max-h-[52svh] overflow-y-auto"
      >
        {renderGroups.map((group) => {
          const isEmpty = !group.loading && !group.error && group.results.length === 0;
          return (
            <section
              key={group.key}
              role="group"
              aria-label={group.title}
              className="mb-1 last:mb-0"
            >
              <div
                role="presentation"
                className="flex items-baseline justify-between gap-3 px-2 pt-2 pb-1"
              >
                <h3 className="text-xs font-semibold text-fg-muted">
                  {group.title}
                </h3>
                {isEmpty && (
                  <span className="text-2xs text-fg-subtle">
                    No matches{trimmed === '' ? '' : ` for “${trimmed}”`}
                  </span>
                )}
              </div>

              {/*
               * ⛔ **THE PER-GROUP `note` IS NO LONGER RENDERED — owner's
               * helper-text ruling, 2026-09-05.** It sat under each group
               * heading and carried that source's scope: *"Matches the deal name
               * only."*, *"Matches the product name only, and opens the Products
               * list — a product has no page of its own."*, *"Opens the Users
               * list — there is no page per user."*
               *
               * ⚠️ **It was deliberately shown on an EMPTY group too**, because
               * "no matches" for a search that only ever looked at one column is
               * exactly where the reader needed to be told which column that
               * was. **That is now unsaid on every group**, so a search for a
               * company by its town, or a deal by its customer, reads as the
               * record not existing.
               *
               * **The `note` field stays on `SearchSource` and on the group
               * type**, still populated, still threaded through — a source's
               * scope is a fact about the query, and deleting it would mean
               * re-deriving three of them from `api.ts` to put any of this back.
               */}

              {group.loading && (
                <div role="presentation" className="px-2 py-1">
                  {[0, 1, 2].map((row) => (
                    <div key={row} className="flex items-center gap-2 py-1.5">
                      <Skeleton className="h-4 w-4 rounded-md" />
                      <Skeleton className="h-3 w-40" />
                    </div>
                  ))}
                </div>
              )}

              {group.error && (
                <p
                  role="presentation"
                  className="mx-2 mb-1 rounded-md border border-danger-border bg-danger-soft px-2 py-1.5 text-xs text-danger"
                >
                  Couldn’t search {group.title.toLowerCase()} — {group.error}
                </p>
              )}

              {group.results.map((result) => {
                const rowIndex = indexByKey.get(result.key) ?? -1;
                const isActive = rowIndex === active;
                return (
                  <div
                    key={result.key}
                    id={`${listId}-${result.key}`}
                    role="option"
                    aria-selected={isActive}
                    data-active={isActive}
                    onPointerDown={(event) => {
                      // Commit before the input's blur can move focus.
                      event.preventDefault();
                      go(result);
                    }}
                    onMouseMove={() => {
                      if (rowIndex >= 0) setActiveIndex(rowIndex);
                    }}
                    className={cx(
                      'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5',
                      'transition-colors duration-150 ease-out motion-reduce:transition-none',
                      isActive ? 'bg-surface-hover text-fg' : 'text-fg-secondary',
                    )}
                  >
                    <span aria-hidden="true" className="shrink-0 text-fg-subtle">
                      {result.icon ?? <SearchIcon />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">
                        <Highlighted text={result.label} indices={result.indices} />
                      </span>
                      {result.hint && (
                        <span className="block truncate text-xs text-fg-muted">{result.hint}</span>
                      )}
                    </span>
                    {/* Not colour alone: the active row is tinted AND carries the Enter glyph. */}
                    {isActive && (
                      <span aria-hidden="true" className="shrink-0 text-fg-subtle">
                        <EnterIcon />
                      </span>
                    )}
                  </div>
                );
              })}
            </section>
          );
        })}

        {searching && flat.length === 0 && !anyLoading && (
          <p className="px-2 py-6 text-center text-xs text-fg-muted">No matches for “{trimmed}”.</p>
        )}

        {/*
         * ⛔ **THE BELOW-THRESHOLD LINE WAS REMOVED — owner's helper-text ruling,
         * 2026-09-05.** Gated on `!searching && trimmed !== ''`, it read
         * `Keep typing — records are searched from {MIN_TERM} characters.`
         *
         * ⚠️ **`MIN_TERM` still gates the request and the gap is now silent.** A
         * one- or two-character term fires NO record search at all, so the
         * palette shows the page group and nothing else — which is exactly what
         * "there are no matching records" looks like. The reader gets no signal
         * that a third character would change the answer.
         */}
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Trigger                                                                    */
/* -------------------------------------------------------------------------- */

const IS_APPLE =
  typeof navigator !== 'undefined' && /mac|iphone|ipad|ipod/i.test(navigator.userAgent);
const SHORTCUT_LABEL = IS_APPLE ? '⌘K' : 'Ctrl K';

/**
 * The header search affordance: a full-width trigger from `md` up, an icon
 * button below it, and the palette itself. ⌘K / Ctrl-K toggles it from
 * anywhere in the app.
 */
export function GlobalSearch() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;
      if (event.altKey) return;
      // The browser's own ⌘K goes to the address bar; the app claims it first.
      event.preventDefault();
      setOpen((current) => !current);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      <div className="flex min-w-0 flex-1 items-center justify-end md:max-w-xs md:justify-start">
        <Button
          variant="secondary"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-keyshortcuts="Meta+K Control+K"
          leadingIcon={<SearchIcon />}
          /*
           * `max-md:hidden`, NOT `hidden md:inline-flex`. `Button` bakes in a
           * base `inline-flex`, and two same-property utilities resolve by
           * stylesheet order — so `hidden` lost and this rendered at 30×44 on
           * a phone as a focusable duplicate of the icon button below. A
           * variant sorts after every base utility, so this one always wins.
           */
          className="w-full justify-start px-2.5 font-normal text-fg-subtle hover:text-fg-secondary max-md:hidden"
        >
          <span className="min-w-0 flex-1 truncate text-left">Search SEED EMERGE</span>
          <kbd className="shrink-0 rounded-sm border border-border px-1 py-px font-sans text-2xs text-fg-muted">
            {SHORTCUT_LABEL}
          </kbd>
        </Button>

        {/* Below `md` the header has no room for the full control. */}
        <Button
          variant="ghost"
          iconOnly
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Search SEED EMERGE"
          leadingIcon={<SearchIcon />}
          className="md:hidden"
        />
      </div>

      {/*
       * Keyed on `open` so every opening starts from a blank palette. A reset
       * effect would do the same thing one render later, and yesterday's term
       * with its stale rows is the exact thing that makes a palette feel wrong.
       */}
      <GlobalSearchPalette
        key={open ? 'open' : 'closed'}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
